/*
 * paex-wishlist-pdp.js — PDP wishlist button (Swym) — PE, 2026-08-27.
 *
 * The third and last theme-owned wishlist control. Same deal as the other two:
 * the theme renders and styles the heart, Swym keeps the list.
 *   - header launchers  → snippets/paex-wishlist-launcher.liquid
 *   - PLP card hearts   → assets/rcc-card-components.js §1
 *   - PDP heart         → HERE + snippets/paex-wishlist-pdp.liquid
 *
 * Why we took the PDP over from Swym's own app block / injection:
 *   1. Placement was unreliable per template. Swym positions its button
 *      absolutely inside whatever its selector matches (live config:
 *      `media-gallery.media-gallery.hover-trigger`), but nothing in the theme
 *      makes `media-gallery` a containing block, so the button escaped to
 *      whichever ancestor happened to be positioned — and that differs across
 *      the four .product-media-layout--* branches. It landed top-left on the
 *      stack/thumbnail-bottom templates and nowhere visible on the
 *      thumbnail-right ones (product.pdp-size-charts).
 *   2. Late paint. Nothing appeared until Swym's script had loaded AND its
 *      config call had returned — measured at ~2.8s on staging. The heart is
 *      now server-rendered, so it is on the page at first paint and this file
 *      only wires state onto it.
 *   3. It did not match the other two hearts (merchant, 2026-08-27).
 *
 * Coverage is structural: the snippet renders from sections/main-product.liquid,
 * which is the section EVERY product template mounts (all ten of
 * templates/product*.json), so there is no per-template configuration to keep
 * in sync and no selector to go stale.
 *
 * Why this is a separate file rather than a fifth module inside
 * rcc-card-components.js: that file is parser-blocking and gated to
 * collection/search page types (snippets/nimstrata.liquid), because RCC must
 * see its custom elements registered before the app block reads its data
 * requirements. The PDP needs the opposite — deferred, product pages only. The
 * Swym bridge below is therefore a deliberate second copy of the card
 * component's proven logic; if a third consumer ever appears, lift the bridge
 * into its own asset and have both read it.
 */

if (!customElements.get('paex-wishlist-button')) {
  const wishlist = {
    swat: null,
    variants: new Set(),
    components: new Set(),
    /* App-off gate. The verdict is OWNED by
       snippets/paex-wishlist-app-state.liquid — inline in <head>, so
       window.paexWishlistApp already exists here. Tri-state:
         null  Swym has not answered yet
         true  the Wishlist app is on
         false the Wishlist app is off
       The heart hides only on a definite false; that snippet explains why an
       unreadable answer must never count as one. */
    appEnabled: null,
  };

  const readAppState = () => (window.paexWishlistApp ? window.paexWishlistApp.enabled : null);
  wishlist.appEnabled = readAppState();

  const syncAll = () => wishlist.components.forEach((component) => component.updateState());

  const updateFromEvent = (event, added) => {
    const variantId = String(event.detail?.d?.epi || '');
    if (!variantId) return;
    wishlist.variants[added ? 'add' : 'delete'](variantId);
    syncAll();
  };

  /* Idempotent: the app-state snippet's SwymCallbacks entry and this file's
     have no guaranteed order, so whichever learns the real verdict first
     starts the sync and the second one is a no-op. */
  let syncStarted = false;
  const startSync = () => {
    const swat = wishlist.swat;
    if (syncStarted || !swat || wishlist.appEnabled === false) return;
    syncStarted = true;

    swat.fetch((items) => {
      wishlist.variants = new Set((items || []).map((item) => String(item.epi)));
      syncAll();
    });
    swat.evtLayer?.addEventListener('sw:addedtowishlist', (event) => updateFromEvent(event, true));
    swat.evtLayer?.addEventListener('sw:removedfromwishlist', (event) => updateFromEvent(event, false));
  };

  const flushPending = () => wishlist.components.forEach((component) => component.flushPending());

  document.addEventListener('paex:wishlist-app', (event) => {
    wishlist.appEnabled = event.detail ? event.detail.enabled : null;
    syncAll();
    startSync();
    flushPending();
  });

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push((swat) => {
    wishlist.swat = swat;
    wishlist.appEnabled = readAppState();
    syncAll();
    startSync();
    flushPending();
  });

  const BEAT_CLASS = 'paex-wishlist-pdp__button--added';
  const STUCK_MS = 8000;

  customElements.define(
    'paex-wishlist-button',
    class PaexWishlistButton extends HTMLElement {
      connectedCallback() {
        /* Server-rendered by the snippet, so there is nothing to build here —
           only to wire. Bail rather than construct a button, so a markup change
           fails visibly in one place instead of producing a second heart. */
        this.button = this.querySelector('button');
        if (!this.button) return;

        this.onClick = (event) => this.toggle(event);
        this.onAnimationEnd = () => this.button.classList.remove(BEAT_CLASS);
        this.button.addEventListener('click', this.onClick);
        this.button.addEventListener('animationend', this.onAnimationEnd);

        wishlist.components.add(this);
        this.subscribeVariant();
        this.updateState();
      }

      disconnectedCallback() {
        wishlist.components.delete(this);
        clearTimeout(this.stuckTimer);
        if (this.unsubscribeVariant) {
          this.unsubscribeVariant();
          this.unsubscribeVariant = null;
        }
        if (this.button) {
          this.button.removeEventListener('click', this.onClick);
          this.button.removeEventListener('animationend', this.onAnimationEnd);
        }
      }

      /* The heart tracks the SELECTED variant, because that is what Swym
         stores (its `epi`) — saving a product page saves the variant the
         shopper is looking at, and switching variants can therefore flip the
         heart. The theme swaps variants without re-rendering the gallery
         (assets/global.js renderProductInfo → updateMedia only toggles
         classes), so this element survives the change and just needs the new
         id.

         typeof-guarded: webvista and PUB_SUB_EVENTS are top-level consts in
         global.js, never window.* — same check nimstrata.liquid and
         paex-quick-add.js use. global.js is deferred in <head> and this file is
         deferred in the section, so it has always evaluated first; the guard is
         for the case where it fails to load at all, which must not take the
         heart down with it. */
      subscribeVariant() {
        if (typeof webvista === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
        if (typeof webvista.subscribe !== 'function') return;

        this.unsubscribeVariant = webvista.subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const data = event && event.data;
          if (!data || data.sectionId !== this.dataset.section) return;
          const variantId = data.variant && data.variant.id;
          /* An option combination with no matching variant publishes no
             variant. Keep the last known id rather than blanking the heart —
             the theme's own "unavailable" state already carries that message,
             and a dead heart mid-selection reads as a bug. */
          if (!variantId) return;
          this.dataset.variantId = String(variantId);
          this.updateState();
        });
      }

      get productId() {
        return String(this.dataset.productId || '');
      }

      get variantId() {
        return String(this.dataset.variantId || '');
      }

      updateState() {
        /* On the host, not the button: this element is the absolutely
           positioned 2.4rem box, so [hidden] removes the whole overlay without
           disturbing the image. Only a definite false hides it. */
        this.toggleAttribute('hidden', wishlist.appEnabled === false);
        if (!this.button) return;

        const added = wishlist.variants.has(this.variantId);
        this.button.classList.toggle('swym-added', added);
        this.button.setAttribute('aria-pressed', String(added));
        this.button.setAttribute(
          'aria-label',
          added
            ? this.dataset.labelRemove || 'Remove from wishlist'
            : this.dataset.labelAdd || 'Add to wishlist'
        );
      }

      /* One queued press, replayed when Swym boots.

         The card hearts silently no-op on a click before swat exists, which is
         tolerable on a grid of forty tiles. On the PDP the heart is one of the
         page's primary controls and Swym does not boot for ~3s, so a dead tap
         in that window is the single most likely thing a shopper will hit.
         Queueing costs a flag; only the most recent press is kept, so a
         double-tap while waiting resolves to one action.

         The busy state is released either by the replay or by the same
         STUCK_MS timeout the live path uses, so a Swym that never boots leaves
         a clickable heart rather than a bricked one. */
      queuePress() {
        this.pendingPress = true;
        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');
        clearTimeout(this.stuckTimer);
        this.stuckTimer = setTimeout(() => this.release(), STUCK_MS);
      }

      /* Order matters here. Both this file's SwymCallbacks entry and the
         app-state snippet's fire the flush, and the snippet's runs FIRST (it is
         inline in <head>, so it is pushed first) — at which point
         wishlist.swat is still null because our own callback has not run yet.
         So a not-ready flush must leave pendingPress set and let the next one
         do the work; clearing it here would silently swallow the press in the
         normal ordering. */
      flushPending() {
        if (!this.pendingPress) return;
        if (wishlist.appEnabled === false) return this.release(); // dead control — drop it
        if (!wishlist.swat) return; // not ready; a later flush will catch it
        this.pendingPress = false;
        this.commit(); // re-arms the busy state and the timer, so no release first
      }

      release() {
        clearTimeout(this.stuckTimer);
        this.pendingPress = false;
        if (!this.button) return;
        this.button.disabled = false;
        this.button.removeAttribute('aria-busy');
      }

      toggle(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.button.disabled || wishlist.appEnabled === false) return;
        if (!wishlist.swat) return this.queuePress();
        this.commit();
      }

      commit() {
        const productId = this.productId;
        const variantId = this.variantId;
        /* release(), not a bare return: commit() is reached from the queued
           path with the button already disabled, so bailing silently would
           leave it that way for the life of the page. */
        if (!productId || !variantId) return this.release();

        const url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
        url.search = '';
        const item = { empi: Number(productId), epi: Number(variantId), du: url.href };
        const added = wishlist.variants.has(variantId);

        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');

        /* Swym answers NEITHER callback when the Wishlist app is off on the
           account (rapps returns {app:"Wishlist", enabled:false}) — seen live
           2026-08-21. Without this the button stays disabled for the life of
           the page, so the heart is dead to every later click even once the
           app is back. The timeout only restores clickability; it deliberately
           does not touch the wishlist state, which never changed. */
        clearTimeout(this.stuckTimer);
        this.stuckTimer = setTimeout(() => this.release(), STUCK_MS);

        wishlist.swat[added ? 'removeFromWishList' : 'addToWishList'](
          item,
          () => {
            wishlist.variants[added ? 'delete' : 'add'](variantId);
            this.release();
            syncAll();
            if (!added) this.playBeat(); // adds only — removal needs no confirmation
          },
          () => this.release()
        );
      }

      /* Click path only, never updateState() — that also runs for the initial
         swat.fetch() sync and would beat the heart on page load for a product
         the shopper had already saved. Same rule as rcc-card-components.js. */
      playBeat() {
        this.button.classList.remove(BEAT_CLASS);
        void this.button.offsetWidth; // reflow, so a re-add inside the window restarts it
        this.button.classList.add(BEAT_CLASS);
      }
    }
  );
}
